import { isNil, isNotNil } from "@etudescarre/utils";
import { type CSSProperties, useLayoutEffect, useState } from "react";

// Two-row table headers with row headers spanning several rows (904, 905),
// optionally under a sticky table title (910 contracts panel).
// Safari only honours `position: sticky` on table cells, not on <thead>/<tr>:
// pin every header cell. The offsets come from the measured title and header
// heights (CSS vars set on a common ancestor), not from hard-coded values:
// Safari sizes table cells differently, which left a strip between the two
// sticky rows where scrolled text showed through. The second row pins exactly
// where it sits at rest, so it doesn't move on the first scroll.
// `--sticky-title` is only set by the hook that measures a title, so a table
// nested under another section's sticky title inherits that offset.
export const stickyTitleClass = "sticky top-0 z-30";
export const stickyHeadFirstRowClass =
  "sticky top-[var(--sticky-title,0px)] z-20";
export const stickyHeadSecondRowClass =
  "sticky top-[calc(var(--sticky-title,0px)+var(--sticky-head-1,40px))] z-20";
// Content of a cell spanning several rows: stays pinned under the header while
// those rows scroll, and leaves with its last row.
export const stickyRowHeaderContentClass =
  "sticky top-[calc(var(--sticky-title,0px)+var(--sticky-head-total,80px))]";

type StickyOffsets = { title: number | null; first: number; total: number };

/**
 * Measures the title and the two header rows so the sticky offsets match what
 * the browser actually rendered. Callback refs: a title or table mounted after
 * the first render (empty state, then a first row) is measured too.
 */
export function useStickyHeaderOffsets() {
  const [title, setTitle] = useState<HTMLElement | null>(null);
  const [thead, setThead] = useState<HTMLTableSectionElement | null>(null);
  const [offsets, setOffsets] = useState<StickyOffsets>({
    title: null,
    first: 40,
    total: 80,
  });

  useLayoutEffect(() => {
    if (isNil(title) && isNil(thead)) return;
    const measure = () => {
      setOffsets({
        title: title?.getBoundingClientRect().height ?? null,
        first: thead?.rows.item(0)?.getBoundingClientRect().height ?? 40,
        total: thead?.getBoundingClientRect().height ?? 80,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (isNotNil(title)) observer.observe(title);
    if (isNotNil(thead)) observer.observe(thead);
    return () => observer.disconnect();
  }, [title, thead]);

  const style: CSSProperties & Record<`--${string}`, string> = {
    ...(isNotNil(offsets.title) && { "--sticky-title": `${offsets.title}px` }),
    "--sticky-head-1": `${offsets.first}px`,
    "--sticky-head-total": `${offsets.total}px`,
  };

  return { titleRef: setTitle, headerRef: setThead, style };
}
